import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Download, Users, Mail, Award, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { GardenPlant } from "@/components/client/Garden";
import { downloadCertificate } from "@/lib/certificate";

const TITLE = { century: "Welcome to the Century Club", rooted: "has taken root" };
const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

function CertificateChoice({ m, stage, tid = "" }) {
  const [withName, setWithName] = useState(false);
  const [name, setName] = useState("");
  return (
    <div className="space-y-2" data-testid={`${tid}certificate-choice`}>
      <label className="flex items-center gap-2 text-xs text-stone-700">
        <input type="checkbox" checked={withName} onChange={(e) => setWithName(e.target.checked)} className="accent-amber-600" data-testid={`${tid}certificate-name-toggle`} /> Put my name on it
      </label>
      {withName && <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Name as you'd like it to read" data-testid={`${tid}certificate-name`}
        className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm focus:border-amber-600 outline-none" />}
      <button type="button" onClick={() => downloadCertificate(m, stage, withName ? name.trim() : "").then(() => toast.success("Certificate saved. Fridge-ready."))}
        data-testid={`${tid}certificate-download`} className="amber-btn text-xs py-2 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Download my certificate</button>
    </div>
  );
}

/** Full-screen, once-only ceremony for the Century Club / first Rooted habit. */
export function MilestoneCelebration({ milestone: m, game, onClose }) {
  const [showOnCard, setShowOnCard] = useState(false);
  useEffect(() => { celebrate(true); }, []);
  const stage = (game?.level?.index || 1) - 1;
  const done = async () => {
    if (!m.demo) { try { await api.patch(`/milestones/${m.id}`, { celebrated: true, show_on_card: showOnCard }); } catch { /* shown once regardless */ } }
    onClose();
  };
  return createPortal(
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-[#f7f1e6]" data-testid="milestone-celebration" data-kind={m.kind}>
      <button onClick={done} aria-label="Close" data-testid="milestone-close" className="fixed top-4 right-4 p-2 rounded-full bg-white/80 text-stone-600 hover:text-stone-900 shadow"><X className="w-5 h-5" /></button>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl mx-auto px-6 pt-24 pb-14 text-center space-y-6">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-rose-800">{m.kind === "century" ? "The Century Club" : "Rooted"}</div>
        <GardenPlant stage={stage} progress={1} century={m.kind === "century"} className="w-56 h-72 mx-auto" />
        {m.kind === "century"
          ? <div className="font-display font-extrabold text-stone-900 text-[7rem] leading-none" data-testid="milestone-number">100</div>
          : <div className="font-display font-extrabold text-stone-900 text-4xl sm:text-5xl" data-testid="milestone-habit">{m.habit_title}</div>}
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900">{m.kind === "century" ? TITLE.century : TITLE.rooted}</h1>
        <p className="text-stone-600 max-w-xl mx-auto">
          {m.kind === "century" ? "One hundred times you showed up for yourself. Not perfectly — that was never the point — but again and again. A stone with your number now sits in your garden, for good."
            : "It climbed every rung of its ladder and held at the top two weeks running. A ribbon now marks it in your garden, for good."}
          {" "}Dated {fmt(m.earned_at)}.
        </p>
        <div className="warm-card p-5 text-left max-w-md mx-auto space-y-4">
          <CertificateChoice m={m} stage={stage} tid="celebration-" />
          <label className="flex items-start gap-2 text-xs text-stone-700 border-t border-stone-100 pt-3">
            <input type="checkbox" checked={showOnCard} onChange={(e) => setShowOnCard(e.target.checked)} className="accent-emerald-700 mt-0.5" data-testid="celebration-show-on-card" />
            <span><Users className="w-3.5 h-3.5 inline mr-1 text-emerald-700" />Show my membership on my community card <span className="text-stone-500">(off by default — nobody sees it unless you choose)</span></span>
          </label>
          <p className="text-[11px] text-stone-500 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> A short note from Rose City arrives in the app tomorrow.</p>
        </div>
        <button onClick={done} data-testid="milestone-done" className="amber-btn px-8">Back to my garden</button>
      </motion.div>
    </div>
  , document.body);
}

/** Letter-like card at the top of Today once the note has gone out. */
export function MilestoneNoteCard({ note, onDismiss }) {
  useEffect(() => { if (!note.note_seen_at) api.patch(`/milestones/${note.id}`, { note_seen: true }).catch(() => {}); }, [note]);
  return (
    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-rose-200 bg-[#fdf8f1] p-5 relative" data-testid={`milestone-note-${note.kind}`}>
      <button onClick={onDismiss} aria-label="Dismiss" data-testid="milestone-note-dismiss" className="absolute top-3 right-3 p-1 rounded-lg text-stone-400 hover:text-stone-700"><X className="w-4 h-4" /></button>
      <div className="text-[10px] font-bold uppercase tracking-widest text-rose-800 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> A note for you</div>
      <p className="mt-3 text-stone-800 whitespace-pre-wrap leading-relaxed" data-testid="milestone-note-text">{note.note_text}</p>
      <p className="mt-3 text-sm text-stone-600 italic">— Rose City</p>
      {note.clinician_line && <blockquote className="mt-4 border-l-2 border-emerald-300 pl-3 text-sm text-stone-700" data-testid="milestone-note-clinician-line">{note.clinician_line}<span className="block text-xs text-stone-500 mt-1">— your coach</span></blockquote>}
    </motion.div>
  );
}

/** Progress tab: permanent memberships with date, certificate and the community-card choice. */
export function MilestonesSection({ data, game, onChange }) {
  if (!data?.memberships?.length) return null;
  const stage = (game?.level?.index || 1) - 1;
  const toggle = async (m) => { await api.patch(`/milestones/${m.id}`, { show_on_card: !m.show_on_card }); onChange(); };
  return (
    <section className="warm-card p-6 space-y-4" data-testid="milestones-section">
      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-rose-800"><Award className="w-3.5 h-3.5" /> Milestones</div>
      {data.memberships.map((m) => (
        <div key={m.id} className="rounded-2xl border border-stone-200 p-4 space-y-3" data-testid={`milestone-${m.kind}${m.habit_id ? `-${m.habit_id}` : ""}`}>
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h3 className="font-display text-lg font-bold text-stone-900">{m.kind === "century" ? "Century Club member" : `Rooted · ${m.habit_title}`}</h3>
            <span className="text-xs text-stone-500">since {fmt(m.earned_at)}</span>
          </div>
          {m.note_text && <p className="text-sm text-stone-700 whitespace-pre-wrap border-l-2 border-rose-200 pl-3">{m.note_text}{m.clinician_line && <span className="block mt-2 text-stone-600">{m.clinician_line} <em className="text-stone-500">— your coach</em></span>}</p>}
          <CertificateChoice m={m} stage={stage} tid={`${m.kind}-`} />
          <label className="flex items-center gap-2 text-xs text-stone-700">
            <input type="checkbox" checked={!!m.show_on_card} onChange={() => toggle(m)} className="accent-emerald-700" data-testid={`milestone-show-on-card-${m.kind}`} /> Show on my community card
          </label>
        </div>
      ))}
    </section>
  );
}
